import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import {
	ILeagueAddParams,
	ILeague,
	ILeagueUpdateParams,
	ILeagueLeaveParams,
	IJoinToLeagueParams,
	ILeagueInviteParams,
	ILeagueDeleteUserParams,
	IGetMyLeaguesParams,
	IGetJoinLeaguesParams,
	IGetLeagueParams,
	IGetLeagueUsersParams,
	ILeagueUser,
	IRemoveLeagueParams,
} from "data/types/leagues";
import {AxiosResponse} from "axios";
import type {IApiResponse, IHttpClientService} from "data/services/http";
import {IPagination} from "data/types/common";

export interface ILeaguesApiProvider {
	create: (variables: ILeagueAddParams) => Promise<AxiosResponse<IApiResponse<ILeague>>>;

	updatePredictorLeague: (
		variables: ILeagueUpdateParams
	) => Promise<AxiosResponse<IApiResponse<ILeague>>>;

	leavePredictorLeague: (
		variables: ILeagueLeaveParams
	) => Promise<AxiosResponse<IApiResponse<Record<string, never>>>>;

	joinPredictorLeague: (
		variables: IJoinToLeagueParams
	) => Promise<AxiosResponse<IApiResponse<ILeague>>>;

	invitePredictorLeague: (
		variables: ILeagueInviteParams
	) => Promise<AxiosResponse<IApiResponse<Record<string, never>>>>;

	removeUserFromPredictorLeague: (
		variables: ILeagueDeleteUserParams
	) => Promise<AxiosResponse<IApiResponse<Record<string, never>>>>;

	removeLeague: (variables: IRemoveLeagueParams) => Promise<AxiosResponse<IApiResponse>>;

	myPredictorLeagues: (
		variables: IGetMyLeaguesParams
	) => Promise<AxiosResponse<IApiResponse<{leagues: ILeague[]; nextPage: boolean}>>>;

	showForJoinPredictorLeague: (
		variables: IGetJoinLeaguesParams
	) => Promise<AxiosResponse<IApiResponse<{leagues: ILeague[]; nextPage: boolean}>>>;

	getPredictorLeague: (
		variables: IGetLeagueParams
	) => Promise<AxiosResponse<IApiResponse<ILeague>>>;

	getPredictorLeagueUsers: (
		variables: IGetLeagueUsersParams
	) => Promise<AxiosResponse<IApiResponse<IPagination<{users: ILeagueUser[]}>>>>;
}

@injectable()
export class LeaguesApiProvider implements ILeaguesApiProvider {
	constructor(@inject(Bindings.ApiHTTPClient) private _http: IHttpClientService) {}

	create = ({locale, ...params}: ILeagueAddParams) =>
		this._http.post<IApiResponse<ILeague>>(`${locale}/league`, params);

	updatePredictorLeague = ({locale = "", leagueId, ...params}: ILeagueUpdateParams) =>
		this._http.post<IApiResponse<ILeague>>(`${locale}/league/${leagueId}`, params);

	leavePredictorLeague = ({locale, leagueId}: ILeagueLeaveParams) =>
		this._http.post<IApiResponse<Record<string, never>>>(`${locale}/league/${leagueId}/leave`);

	joinPredictorLeague = ({locale, code}: IJoinToLeagueParams) =>
		this._http.post<IApiResponse<ILeague>>(`${locale}/league/${code}/join`);

	invitePredictorLeague = ({leagueId, locale, ...params}: ILeagueInviteParams) =>
		this._http.post<IApiResponse<Record<string, never>>>(
			`${locale}/league/${leagueId}/invite`,
			params
		);

	removeUserFromPredictorLeague = ({locale, leagueId, userId}: ILeagueDeleteUserParams) =>
		this._http.post<IApiResponse<Record<string, never>>>(
			`${locale}/league/${leagueId}/user/${userId}`
		);

	removeLeague = ({locale, leagueId}: IRemoveLeagueParams) =>
		this._http.post<IApiResponse>(`${locale}/league/${leagueId}/delete`);

	myPredictorLeagues = ({locale, ...params}: IGetMyLeaguesParams) =>
		this._http.get<IApiResponse<{leagues: ILeague[]; nextPage: boolean}>>(
			`${locale}/leagues`,
			params
		);

	showForJoinPredictorLeague = ({locale, ...params}: IGetJoinLeaguesParams) =>
		this._http.get<IApiResponse<{leagues: ILeague[]; nextPage: boolean}>>(
			`${locale}/league/show-for-join`,
			params
		);

	getPredictorLeague = ({locale, leagueId}: IGetLeagueParams) =>
		this._http.get<IApiResponse<ILeague>>(`${locale}/league/${leagueId}`);

	getPredictorLeagueUsers = ({locale, leagueId, ...params}: IGetLeagueUsersParams) =>
		this._http.get<IApiResponse<IPagination<{users: ILeagueUser[]}>>>(
			`${locale}/league/${leagueId}/league-users`,
			params
		);
}
